import { useEffect, useState } from 'react';
import { isUseO2Token } from 'reactApp/api/O2AuthClient';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import type { CloudFile } from 'reactApp/modules/storage/storage.types';
import { WorkspaceSuperAppSourceLoader } from 'reactApp/services';
import { getWorkspaceSuperAppService } from 'reactApp/services/WorkspaceSuperApp.class';
import { o2AuthedSourceLoader } from 'reactApp/ui/ReactViewer/O2AuthedSourceLoader';

export const preparePdfSource = (src: string, { hideToolbar }: { hideToolbar: boolean }): string => {
    return hideToolbar ? `${src}#toolbar=0` : src;
};

/**
 * Хук для авторизации источника (src).
 * Автоматически выбирает способ авторизации:
 * - SuperApp AccessToken - если включен SuperApp (и файл не передан, либо не является вложением attach)
 * - O2 токен - если включен O2 токен
 * - Без авторизации - в остальных случаях
 */
export const useAuthedSource = (src: string, handleError: () => void, file?: CloudFile): string | undefined => {
    const [authedSource, setAuthedSource] = useState<string | undefined>(undefined);

    const workspaceSuperAppService = getWorkspaceSuperAppService();
    const isAttach = file && 'type' in file && file.type === 'attach';

    const { isPublic } = getStorage(file?.storage || null);
    const isUseSuperAppAccessToken = workspaceSuperAppService?.isAuthRequired && !isAttach && !isPublic;
    const useO2 = isUseO2Token();

    useEffect(() => {
        if (isUseSuperAppAccessToken) {
            WorkspaceSuperAppSourceLoader.getAuthedLink(src).then(setAuthedSource).catch(handleError);
            return;
        }

        if (useO2) {
            o2AuthedSourceLoader
                .getAuthedSourceFromRedirect(src)
                .then(o2AuthedSourceLoader.getObjectUrlFromBlob)
                .then(setAuthedSource)
                .catch(handleError);

            return () => {
                if (authedSource) {
                    o2AuthedSourceLoader.revokeObjectUrl(authedSource);
                }
            };
        }

        setAuthedSource(src);
    }, [src, isUseSuperAppAccessToken, useO2]);

    if (!isUseSuperAppAccessToken && !useO2) {
        return src;
    }

    return authedSource;
};
